import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { safeLog } from '../utils/logSafe';
import {
    RecaudacionMasivaApiService,
    ProgresoRecaudacionResponse
} from '../features/recaudacionMasiva/registrarRecaudacionMasiva/services/recaudacionMasivaApiService';

type ProgresoRecaudacion = ProgresoRecaudacionResponse;

const POLLING_INTERVAL_MS = 3000;

interface RecaudacionContextType {
    recaudacionId: string | null;
    recaudacionEnProceso: boolean;
    recaudacionCompletada: ProgresoRecaudacion | null;
    progresoActual: ProgresoRecaudacion | null;
    isConnected: boolean;
    setRecaudacionId: (id: string | null) => void;
    setRecaudacionEnProceso: (enProceso: boolean) => void;
    setRecaudacionCompletada: (completada: ProgresoRecaudacion | null) => void;
    setProgresoActual: (progreso: ProgresoRecaudacion | null) => void;
    limpiarRecaudacion: () => void;
    conectarProgreso: (recaudacionId: string) => Promise<void>;
    desconectarProgreso: () => Promise<void>;
}

const RecaudacionContext = createContext<RecaudacionContextType | undefined>(undefined);

export const useRecaudacionContext = () => {
    const context = useContext(RecaudacionContext);
    if (!context) {
        throw new Error('useRecaudacionContext debe ser usado dentro de RecaudacionProvider');
    }
    return context;
};

export const RecaudacionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [recaudacionId, setRecaudacionId] = useState<string | null>(null);
    const [recaudacionEnProceso, setRecaudacionEnProceso] = useState(false);
    const [recaudacionCompletada, setRecaudacionCompletada] = useState<ProgresoRecaudacion | null>(null);
    const [progresoActual, setProgresoActual] = useState<ProgresoRecaudacion | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const pollingIntervalRef = useRef<number | null>(null);

    const detenerPolling = useCallback(() => {
        if (pollingIntervalRef.current !== null) {
            window.clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    }, []);

    const procesarProgreso = useCallback((progreso: ProgresoRecaudacion) => {
        safeLog('📊 Progreso actualizado por polling (Contexto):', progreso);
        setProgresoActual(progreso);

        const estado = progreso.estado?.toLowerCase();
        const procesoTerminado = estado === 'completado' || estado === 'error' || estado === 'cancelado';

        if (procesoTerminado) {
            console.log('🔄 Proceso terminado, actualizando estado (Contexto)');
            setRecaudacionEnProceso(false);
            setRecaudacionCompletada(progreso);
            setRecaudacionId(null); // limpia localStorage vía el useEffect que lo observa
            detenerPolling();
        }
    }, [detenerPolling]);

    const consultarProgreso = useCallback(async (id: string) => {
        if (!id) {
            console.warn('⚠️ ID de recaudación vacío, no consultando progreso');
            return;
        }
        try {
            safeLog('📡 Consultando progreso para ID:', id);
            const progreso = await RecaudacionMasivaApiService.obtenerProgreso(id);
            setIsConnected(true);
            procesarProgreso(progreso);
        } catch (error) {
            setIsConnected(false);
            console.warn('⚠️ Error consultando progreso por polling:', error);
        }
    }, [procesarProgreso]);

    // Iniciar polling automáticamente cuando se asigna un recaudacionId y recaudacionEnProceso
    useEffect(() => {
        if (recaudacionId && recaudacionEnProceso) {
            safeLog('🔁 Iniciando polling para recaudacionId:', recaudacionId);

            // Primera consulta después de 500ms (esperar a que backend procese la inicialización)
            const initialTimeoutId = setTimeout(() => {
                consultarProgreso(recaudacionId);
            }, 500);

            // Consultas periódicas cada 3s
            pollingIntervalRef.current = window.setInterval(() => {
                consultarProgreso(recaudacionId);
            }, POLLING_INTERVAL_MS);

            // Cleanup: limpiar timeout e interval
            return () => {
                clearTimeout(initialTimeoutId);
                if (pollingIntervalRef.current !== null) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
            };
        }
    }, [recaudacionId, recaudacionEnProceso, consultarProgreso]);

    // Detener polling cuando se limpia el recaudacionId
    useEffect(() => {
        if (!recaudacionId) {
            setIsConnected(false);
            detenerPolling();
        }
    }, [recaudacionId, detenerPolling]);

    // Cargar recaudación activa desde localStorage al inicializar
    useEffect(() => {
        const recaudacionActiva = sessionStorage.getItem('recaudacionActiva');
        if (recaudacionActiva) {
            safeLog('🔄 Recuperando recaudación activa desde sessionStorage:', recaudacionActiva);
            setRecaudacionId(recaudacionActiva);
            setRecaudacionEnProceso(true);
        }
    }, []);

    // Persistir recaudacionId en localStorage
    useEffect(() => {
        if (recaudacionId) {
            sessionStorage.setItem('recaudacionActiva', recaudacionId);
            safeLog('💾 Guardando recaudación activa en sessionStorage:', recaudacionId);
        } else {
            sessionStorage.removeItem('recaudacionActiva');
        }
    }, [recaudacionId]);

    const conectarProgreso = useCallback(async (id: string) => {
        safeLog('✅ Iniciando recaudación con ID:', id);
        setRecaudacionCompletada(null);
        setProgresoActual(null);
        setRecaudacionId(id);
        setRecaudacionEnProceso(true);
        // El useEffect manejará el polling automáticamente
    }, []);

    const desconectarProgreso = useCallback(async () => {
        detenerPolling();
        setIsConnected(false);
        setRecaudacionId(null);
        setRecaudacionEnProceso(false);
    }, [detenerPolling]);

    const limpiarRecaudacion = useCallback(() => {
        console.log('🧹 Limpiando recaudación (Contexto)');
        setRecaudacionId(null);
        setRecaudacionEnProceso(false);
        setRecaudacionCompletada(null);
        setProgresoActual(null);
        setIsConnected(false);
        detenerPolling();
    }, [detenerPolling]);

    useEffect(() => {
        return () => {
            detenerPolling();
        };
    }, [detenerPolling]);

    const contextValue: RecaudacionContextType = {
        recaudacionId,
        recaudacionEnProceso,
        recaudacionCompletada,
        progresoActual,
        isConnected,
        setRecaudacionId,
        setRecaudacionEnProceso,
        setRecaudacionCompletada,
        setProgresoActual,
        limpiarRecaudacion,
        conectarProgreso,
        desconectarProgreso
    };

    return (
        <RecaudacionContext.Provider value={contextValue}>
            {children}
        </RecaudacionContext.Provider>
    );
}; 